'use client'

import { useEffect, useState } from 'react'

import { buildHeatmapWindow, sameSeriesWindowCovers } from '@/features/chart/lib/orderbook-heatmap'
import type { TVWidgetInstance } from '@/lib/tradingview/types/tv-widget.types'
import { reachesLiveEdge, useOrderbookHeatmap } from '@/service/orderbook-heatmap.hook'
import type { Exchange } from '@/types/generated/common/types'
import type { OrderbookHeatmapRequest } from '@/types/generated/public_api'

const RANGE_DEBOUNCE_MS = 300

function requestKey(request: OrderbookHeatmapRequest | null) {
  if (!request) return ''
  return [
    request.exchange,
    request.symbol,
    request.timeframe,
    request.fromMs,
    request.toMs,
    request.limit,
  ].join(':')
}

/**
 * Fetches heatmap columns for whatever window the chart is showing, re-requesting on pan/zoom.
 * `atLiveEdge` says whether that window runs into the open bar, which is when its live column is
 * worth streaming.
 */
export function useOrderbookHeatmapViewport({
  widget,
  isChartReady,
  enabled,
  exchange,
  symbol,
  timeframe,
}: {
  widget: TVWidgetInstance | null
  isChartReady: boolean
  enabled: boolean
  exchange: Exchange
  symbol: string
  timeframe: number
}) {
  const [request, setRequest] = useState<OrderbookHeatmapRequest | null>(null)

  useEffect(() => {
    if (!enabled || !isChartReady || !widget) {
      setRequest(null)
      return
    }

    const chart = widget.activeChart()
    let debounceId: ReturnType<typeof setTimeout> | null = null

    const capture = () => {
      let next: OrderbookHeatmapRequest | null = null
      try {
        const heatmapWindow = buildHeatmapWindow(chart.getVisibleRange(), timeframe)
        if (heatmapWindow) {
          next = {
            exchange,
            symbol,
            timeframe,
            fromMs: String(heatmapWindow.fromMs),
            toMs: String(heatmapWindow.toMs),
            limit: heatmapWindow.limit,
          }
        }
      } catch {}
      setRequest((current) =>
        requestKey(current) === requestKey(next) ||
        (current && next && sameSeriesWindowCovers(current, next))
          ? current
          : next
      )
    }

    const scheduleCapture = () => {
      if (debounceId) clearTimeout(debounceId)
      debounceId = setTimeout(capture, RANGE_DEBOUNCE_MS)
    }

    capture()
    const visibleRangeSubscription = chart.onVisibleRangeChanged()
    visibleRangeSubscription.subscribe(null, scheduleCapture)

    return () => {
      if (debounceId) clearTimeout(debounceId)
      try {
        visibleRangeSubscription.unsubscribe(null, scheduleCapture)
      } catch {}
    }
  }, [enabled, exchange, isChartReady, symbol, timeframe, widget])

  const heatmap = useOrderbookHeatmap(request)
  return { heatmap, atLiveEdge: request !== null && reachesLiveEdge(request) }
}
